import React, { useContext, useEffect } from 'react';
import {
  AuthorContext,
  useLocalStorage,
  routes,
  InternalLink,
  PageContext
} from 'gatsby-theme-apollo';
import styled from '@emotion/styled';
import moment from 'moment';
import { TextForm } from '../text/text-form';
import Section from '../layout/section';
import { CreateComment } from '../../services/comment-service';

import {
  ByLine,
  Author,
  PostDate,
  Content,
  Spacer,
  AuthorBadge,
  UserTextBox
} from '../text/user-text';

const StyledComments = styled.div(props => ({
  display: `flex`,
  flexDirection: `column`,
  alignItems: `flex-start`,
  fontSize: `18px`,
  borderRadius: props.theme.radius,
  backgroundColor: props.theme.backgroundDarkSemiTransparent,
  maxWidth: `800px`,
  padding: `10px`,
  margin: `0px auto`
}));

const StyledCommentRoot = styled.div({
  display: `flex`,
  flexDirection: `column`,
  alignItems: `flex-start`
});

const Comment = ({ comment, contentAuthorID, signedInAuthorID }) => {
  const { text, id, author, createdAt } = comment;
  const byContentAuthor = contentAuthorID && contentAuthorID === id;
  const bySignedInAuthor = signedInAuthorID === (author && author.id);

  return (
    <StyledCommentRoot>
      <UserTextBox byContentAuthor={byContentAuthor}>
        <ByLine>
          {author && (
            <Author>
              <InternalLink to={routes.User(author.name)}>{author.name}</InternalLink>
              {byContentAuthor && <AuthorBadge>(Author)</AuthorBadge>}
              {bySignedInAuthor && <AuthorBadge>(You)</AuthorBadge>}
            </Author>
          )}
          <PostDate>{moment(createdAt).fromNow()}</PostDate>
        </ByLine>
        <Content>{text}</Content>
      </UserTextBox>
    </StyledCommentRoot>
  );
};

const Comments = ({ contentID, comments = [], contentAuthorID }) => {
  const { author } = useContext(AuthorContext);
  const { logEvent } = useContext(PageContext);
  const cmsComments = comments;

  const [commentText, setCommentText] = useLocalStorage(`${contentID}-comment-input`);
  const [commentIntent, setCommentIntent] = useLocalStorage(`${contentID}-comment-intent`, false);
  const [submittedComments, setSubmittedComments] = useLocalStorage(
    `${contentID}-submitted-comments`,
    []
  );
  const [lastSubmittedCommentText, setLastSubmittedCommentText] = useLocalStorage(
    `${contentID}-comment-last-submitted`
  );

  // Remove comments that have been baked in from the submitted set
  const submittedUnbakedComments = submittedComments.filter(submittedComment => {
    return !cmsComments.find(
      cmsComment =>
        cmsComment.text === submittedComment.text &&
        cmsComment.author &&
        cmsComment.author.id === submittedComment.author.id
    );
  });

  // Merge submitted and CMS-side comments
  const commentsToDisplay = cmsComments.concat(submittedUnbakedComments);
  const sortedComments =
    commentsToDisplay &&
    commentsToDisplay.sort((A, B) => new Date(A.createdAt) - new Date(B.createdAt));

  const SubmitComment = () => {
    if (author && author.id) {
      if (commentText !== lastSubmittedCommentText && commentText !== '') {
        const newComment = {
          text: commentText,
          createdAt: new Date(),
          author: {
            id: author.id,
            name: author.name
          }
        };

        logEvent('submit_comment', { text: newComment.text, contentID });
        setSubmittedComments([...submittedComments, newComment]);
        CreateComment({ authorID: author.id, contentID, text: commentText });
      }
      setCommentText('');
      setLastSubmittedCommentText(commentText);
      setCommentIntent(false);
    }
  };

  useEffect(() => {
    if (author && commentIntent && commentText) {
      SubmitComment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [author]);

  return (
    <Section title="Comments" flushContent solidBackground>
      <StyledComments>
        {sortedComments.map((comment, index) => {
          return (
            <Comment
              comment={comment}
              contentAuthorID={contentAuthorID}
              signedInAuthorID={author && author.id}
              key={index}
            />
          );
        })}
        <Spacer />
        <TextForm
          placeholder={'Type your comment'}
          contentType="Comment"
          value={commentText}
          onSignIn={() => {
            setCommentIntent('submit');
          }}
          onChange={newText => {
            setCommentText(newText);
          }}
          onSubmit={() => {
            SubmitComment();
          }}
        />
      </StyledComments>
    </Section>
  );
};

export { Comments };
